"use client";

import React, { useState, useMemo, useEffect } from "react";
import styles from "./HeroSection.module.scss";
import Text from "@components/atoms/Text";
import { Modal } from "@components/atoms/Modal";
import Image from "next/image";
import assetLoader, { s3UrlLoader } from "@utils/internalImageLoder";
import { HERO_SECTION_DATA } from "./HeroSection.data";
import { useWishlist } from "@context/WishlistContext";
import { usePathname, useRouter } from "next/navigation";
import Button from "@components/atoms/Button";
import { toKebabCase } from "@utils/stringFormatters";
import { formatCurrencyINR } from "@utils/numberFormatter";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import LogoLink from "@components/molecules/LogoLink";
import MobileCitySelectionHandler from "@components/molecules/CitySelectionHandler/MobileCitySelectionHandler";
import { LoginButton } from "@components/molecules/LoginButton";
import { useIntersectionObserver } from "@hooks/use-intersection-observer";
import { getLoginUrl } from "@utils/getLoginUrl";
import { PAGE_TYPES } from "@constants/pageTypes";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { useAppBarUpdate } from "@hooks/useAppBarUpdate";
import { COLLECTION_HEADER_APPBAR_CONFIG } from "@utils/appbarConfig";

interface HeroSectionProps {
  isLoggedIn: boolean;
  savingBalance?: any;
  cityName?: any;
  authCookie?: any;
  userProfile?: any
}

const HeroSection = ({ isLoggedIn, savingBalance, cityName, authCookie, userProfile }: HeroSectionProps) => {
  const { isIntersecting, ref: mobileContainerRef } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.1,
    rootMargin: "0px"
  });
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [isSavingModalOpen, setIsSavingModalOpen] = useState<boolean>(false);
  const { wishlistItems } = useWishlist();
  const balance = formatCurrencyINR(savingBalance);

  useAppBarUpdate(COLLECTION_HEADER_APPBAR_CONFIG);

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const wishlistDisplay = useMemo(() => {
    const wishlistLength = wishlistItems.length;
    if (wishlistLength === 0) {
      return {
        showEmptyState: true,
        displayCars: [],
        showAddIcon: false,
        showCountIndicator: false,
        extraCount: 0,
      };
    }
    if (wishlistLength <= 3) {
      return {
        showEmptyState: false,
        displayCars: wishlistItems.map((car) => 
          `${s3UrlLoader()}/${toKebabCase(car.make)}/${toKebabCase(car.name)}/default/Hero-Transparent.png`
        ),
        showAddIcon: true,
        showCountIndicator: false,
        extraCount: 0,
      };
    }

    return {
      showEmptyState: false,
      displayCars: wishlistItems.slice(0, 3).map((car) => 
        `${s3UrlLoader()}/${toKebabCase(car.make)}/${toKebabCase(car.name)}/default/Hero-Transparent.png`
      ),
      showAddIcon: false,
      showCountIndicator: true,
      extraCount: wishlistLength - 3,
    };
  }, [wishlistItems]);

  const handleSavingModalOpen = async () => {
    setIsSavingModalOpen(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.BUY_HOMEPAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_ncsa",
        current_amount: balance,
        city_name: cityName
      },
    });
  };

  const handleSavingModalClose = () => {
    setIsSavingModalOpen(false);
  };

  const handleSearchClick = async () => {
    showLoader('Loading search...');
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.BUY_HOMEPAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_search",
        city_name: cityName
      },
    });
    router.push("/search/");
  };

  const handleWishlistClick = async () => {
    showLoader('Loading...');

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.BUY_HOMEPAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_wishlist_header",
        wishlisted_car_count: wishlistItems.length || 0,
        city_name: cityName
      },
    });

    if(!isLoggedIn){
      hideLoader();
      await handleLoginFlow(getLoginUrl(window.location.href), LoginSdkConfig.WISHLIST);
      return;
    }

    router.push("/wishlist/");
  };

  return (
    <>
      <div className={styles.desktopContainer}>
        <Text level="h1" className={styles.desktoptitle}>
          {HERO_SECTION_DATA.desktop.title}
        </Text>
        {isLoggedIn && (
          <div className={styles.desktopbuttonContainer}>
            <Button
              className={styles.desktopwishlistButton}
              aria-label="View your car wishlist"
              onButtonClick={handleWishlistClick}
            >
              <div className={styles.desktopWishListSection}>
                {wishlistDisplay.showEmptyState && (
                  <div className={styles.desktopWishlistCars}>
                    <Image
                      src={`${assetLoader()}/icons/Add_dark.svg`}
                      alt="Add to wishlist"
                      width={12}
                      height={12}
                      loading="eager"
                    />
                  </div>
                )}

                {!wishlistDisplay.showEmptyState &&
                  wishlistDisplay.displayCars.map((carImg, index) => (
                    <div key={index} className={styles.desktopWishlistCars}>
                      <Image
                        src={carImg}
                        alt={`Wishlist car ${index + 1}`}
                        fill
                        sizes="(max-width: 768px) 40px, 48px"
                        style={{ objectFit: "contain" }}
                        className={styles.desktopWishlistCarsImage}
                        loading="eager"
                      />
                    </div>
                  ))}

                {!wishlistDisplay.showEmptyState &&
                  wishlistDisplay.showCountIndicator && (
                    <div className={styles.desktopWishlistCars}>
                      <Text className={styles.desktopWishlistCountText}>
                        +{wishlistDisplay.extraCount}
                      </Text>
                    </div>
                  )}
              </div>
              <Text className={styles.desktopWishlistButtonText}>
                {HERO_SECTION_DATA.desktop.wishlistButtonText}
              </Text>
              <Image
                src={`${assetLoader()}/icons/chevron_right_dark.svg`}
                alt="Add to wishlist"
                width={19}
                height={19}
                loading="eager"
              />
            </Button>
            {
              savingBalance > 0 && (
                <Button
                  className={styles.desktopsavingButton}
                  onButtonClick={handleSavingModalOpen}
                  aria-label="View your savings wallet"
                >
                  <span className={styles.desktopbuttonBoldText}>
                    {balance}
                  </span>{" "}
                  {HERO_SECTION_DATA.desktop.savingButtonText}
                  <Image
                    src={`${assetLoader()}/icons/chevron_right_dark.svg`}
                    alt="Add to wishlist"
                    width={19}
                    height={19}
                    loading="lazy"
                  />
                </Button>
              )
            }
          </div>
        )}
      </div>

      <div className={styles.mobileHeaderWapper}>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileLeftSection}>
            <div className={styles.mobileLogoLocationContainer}>
              <LogoLink 
                cityName={cityName}
                className={styles.logoLink}
                logoClassName={styles.logoSize}
              />
              <MobileCitySelectionHandler showIcon={true} />
            </div>
          </div>

          <div className={styles.mobileSearchAvatarContainer}>
            {savingBalance <= 0 ? (
              <Image
                src={`${assetLoader()}/icons/WishlistIcon.svg`}
                alt="Add to wishlist"
                width={22}
                height={22}
                loading="eager"
                onClick={handleWishlistClick}
              />
            ) : (
              !isIntersecting && (
                <>
                  {
                    savingBalance > 0 && (
                      <Image 
                      src={`${assetLoader()}/icons/WalletIcon.svg`}
                      alt="Saving Wallet"
                      width={22}
                      height={22}
                      loading="eager"
                      onClick={handleSavingModalOpen}
                    />
                    )
                  }
                  <Image
                    src={`${assetLoader()}/icons/WishlistIcon.svg`}
                    alt="Add to wishlist"
                    width={22}
                    height={22}
                    loading="eager"
                    onClick={handleWishlistClick}
                  />
                </>
              )
            )}
            <LoginButton authCookie={authCookie} userProfile={userProfile} cityName={cityName} />
          </div>
        </div>
      </div>

      <div ref={mobileContainerRef} className={`${styles.mobileContainer} ${isLoggedIn && savingBalance > 0 ? styles.mobileContainerLoggedIn : styles.mobileContainerLoggedOut}`}>
        {isLoggedIn && savingBalance > 0 && (
          <div className={styles.mobleContent}>
            <div
              className={styles.mobleWishListContainer}
              onClick={handleWishlistClick}
            >
              <Text className={styles.mobleWishListText}>
                {HERO_SECTION_DATA.mobile.wishlistText}
              </Text>
              <div className={styles.mobileWishListSection}>
                {wishlistDisplay.showEmptyState && (
                  <div className={styles.addWishlistButton}>
                    <Image
                      src={`${assetLoader()}/icons/WishlistIcon.svg`}
                      alt="Add to wishlist"
                      width={18}
                      height={18}
                      loading="lazy"
                    />
                    <Text className={styles.addWishlistButtonText}>
                      Wishlist car
                    </Text>
                  </div>
                )}

                {!wishlistDisplay.showEmptyState &&
                  wishlistDisplay.displayCars.map((carImg, index) => (
                    <div key={index} className={styles.wishlistCars}>
                      <Image
                        src={carImg}
                        alt={`Wishlist car ${index + 1}`}
                        fill
                        sizes="(max-width: 768px) 32px, 40px"
                        style={{ objectFit: "contain" }}
                        className={styles.wishlistCarsImage}
                      />
                    </div>
                  ))}

                {!wishlistDisplay.showEmptyState &&
                  wishlistDisplay.showAddIcon && (
                    <div className={styles.wishlistCars}>
                      <Image
                        src={`${assetLoader()}/icons/Add.svg`}
                        alt="Add more to wishlist"
                        width={18}
                        height={18}
                        loading="lazy"
                        className={styles.wishlistCarsAddIcon}
                      />
                    </div>
                  )}

                {!wishlistDisplay.showEmptyState &&
                  wishlistDisplay.showCountIndicator && (
                    <div className={styles.wishlistCars}>
                      <Text className={styles.wishlistCountText}>
                        + {wishlistDisplay.extraCount}
                      </Text>
                    </div>
                  )}
              </div>
            </div>
            <div
              className={styles.mobleSavingContainer}
              onClick={handleSavingModalOpen}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleSavingModalOpen();
                }
              }}
              aria-label="View your savings wallet"
            >
              {savingBalance > 0 ? (
                <>
                  <Text className={styles.mobleSavingHeadText}>
                    New car savings balance
                  </Text>
                  <div className={styles.mobleSavingHeadValue}>
                    <Image 
                      src={`${assetLoader()}/icons/WalletIcon.svg`}
                      alt="Saving Wallet"
                      width={18}
                      height={18}
                      loading="eager"
                    />
                    <Text className={styles.mobleSavingSubHeadText}>
                      {balance}
                    </Text>
                  </div>
                </>
              ) : (
                <>
                  <Text className={styles.mobleSavingHeadText}>
                    Car saving balance
                  </Text>
                  <div className={styles.addWishlistButton}>
                    <Text className={styles.addWishlistButtonText}>
                      Check balance
                    </Text>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <div
          className={styles.mobileSearchBar}
          onClick={handleSearchClick}
        >
          <Image
            src={`${assetLoader()}/icons/search_dark.svg`}
            alt="Hero background pattern"
            width={21}
            height={21}
            loading="lazy"
          />
          <Text className={styles.mobileSearchBarText}>
            {HERO_SECTION_DATA.mobile.searchBarText}
          </Text>
        </div>
        {
          savingBalance > 0 && 
          <Image
            src={`${assetLoader()}/images/Desert wide.png`}
            alt="Hero background pattern"
            fill
            className={styles.mobleImage}
            loading="lazy"
          />
        }
      </div>

      {isLoggedIn && isSavingModalOpen && (
        <Modal
          isOpen={isSavingModalOpen}
          onClose={handleSavingModalClose}
          overlayClassName={styles.modalOverlay}
          contentClassName={styles.modalContent}
          headerClassName={styles.headerClassName}
          closeButtonClassName={styles.closeButtonClassName}
        >
          <div className={styles.savingModalContainer}>
            <Image
              src={HERO_SECTION_DATA.savingModal.image}
              alt={HERO_SECTION_DATA.savingModal.alt}
              width={96}
              height={96}
              priority
            />
            <Text className={styles.savingModalheading}>
              {HERO_SECTION_DATA.savingModal.heading}
              <span className={styles.savingModalheadingSpan}>
                {balance}
              </span>
              {HERO_SECTION_DATA.savingModal.headingAfterSpan}
            </Text>
            <div className={styles.savingModalContent}>
              <Text className={styles.savingModalContentHeading}>
                {HERO_SECTION_DATA.savingModal.contentHeading}
                <span className={styles.savingModalContentHeadingSpan}>
                  {HERO_SECTION_DATA.savingModal.contentHeadingSpan}
                </span>
              </Text>
              <ul className={styles.savingModalUL}>
                {HERO_SECTION_DATA.savingModal.points.map((point, index) => (
                  <li key={index} className={styles.savingModalLI}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <Button
              className={styles.savingButton}
              onClick={handleSavingModalClose}
              aria-label="Close savings modal"
            >
              {HERO_SECTION_DATA.savingModal.buttonText}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default HeroSection;
